import cn from 'clsx';
import {priceHelper, siteSettingsV2} from '@helpers/config-master.mjs';
import {LoadAffirmScript} from '@components/affirm/load-affirm-script';
import {niceMoney} from '@helpers/common/number.mjs';
import {formatPriceNumeric} from '@components/product/global/formatPriceNumeric';
import {useSession} from 'next-auth/react';
import Image from 'next/image';
import StrikeThroughPrice from '@imageTheme/mobile/BlackFriday2023/BF2023-X.webp';

export const SingleProductFocusPricing = ({product, isDailyDeal}) => {
  const {data: session} = useSession();
  const user = session?.user;
  const isDealer = user?.isDealer;

  let selectedVariant = product;
  let salePriceValue = priceHelper.getFinalPrice(selectedVariant);
  let isSaleEnabled = priceHelper.saleEnabledFor(selectedVariant);
  let isDailyDealSale = isDailyDeal;
  // console.log({salePriceValue, isSaleEnabled});

  let PriceComponent = NormalPricing;
  if (isDealer) {
    PriceComponent = DealerPricing;
  } else if (isDailyDealSale) {
    PriceComponent = DailyDealSalePricing;
  } else if (isSaleEnabled) {
    PriceComponent = SalePricing;
  }

  return (
    <div>
      <div>
        <PriceComponent product={product}/>
      </div>

      <div id='div-affirm' className={'text-left'}>
        <LoadAffirmScript price={salePriceValue}/>
      </div>
    </div>
  );
};

const NormalPricing = ({product}) => {
  const selectedVariant = product?.selectedVariant ?? product;
  const {map_price, msrp_price} = useAllPrices(selectedVariant);
  return (
    <>
      <div className='flex flex-row gap-3 items-center'>
        <div className='text-lg lg:text-3xl whitespace-nowrap'>
          <span className={cn('font-bold')} style={{color: 'rgba(4,43,138, 1)'}}>
            {niceMoney(map_price)}
          </span>
        </div>

        <div
          className='text-gray-500 w-fit text-sm lg:text-lg relative font-normal flex line-through'>
          {`MSRP: ${niceMoney(msrp_price)}`}
        </div>
      </div>
    </>
  );
};

const SalePricing = ({product}) => {
  // console.log({salePricing_product: product});
  const selectedVariant = product?.selectedVariant ?? product;
  const {sale_price, map_price, msrp_price} = useAllPrices(selectedVariant);
  // console.log({salePrice: sale_price, mapPrice: map_price, msrpPrice: msrp_price})

  let isInStock = product?.currentlyInStock; /* useProductStock?.isInStock;*/
  const iSSaleEnabled = priceHelper.saleEnabledFor(selectedVariant);

  const salePrice = niceMoney(sale_price);
  const mapPrice = niceMoney(map_price);

  return (
    <>
      <div className='flex flex-row gap-3 items-center justify-start'>
        <div className='text-lg lg:text-2xl whitespace-nowrap'>
          <span
            className={'font-bold'}
            style={{
              color: 'rgba(4,43,138, 1)',
              fontWeight: '500'
            }}>{`Memorial Day Sale Price: ${salePrice}`}</span>
        </div>
        <div className='text-neutral-700 text-base lg:text-lg relative flex line-through'>
          <div className={'relative'}>
            {mapPrice}
            <div className={'absolute top-0 right-0 w-full h-full flex items-center justify-start'}>
              <div className={'opacity-80'}>
                <Image src={StrikeThroughPrice} alt='Strike Through Price'/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const DailyDealSalePricing = ({product}) => {
  //console.log({salePricing_product: product});
  const selectedVariant = product?.selectedVariant ?? product;
  const {sale_price, map_price, msrp_price} = useAllPrices(selectedVariant);
  // console.log({salePrice: sale_price, mapPrice: map_price, msrpPrice: msrp_price})

  const dailyDealDiscountPercentage = priceHelper.salePercentageDailyDeal;
  // console.log({dailyDealDiscountPercentage});

  const saleAttributes = siteSettingsV2.getSaleSettings();
  const specialPriceText = saleAttributes?.layoutSettings?.sideCartSpecialPriceText ?? siteSettingsV2?.layoutSettings?.sideCartSpecialPriceText;


  const mapPrice = niceMoney(map_price);
  const salePrice = niceMoney(sale_price);

  return (
    <>
      <div className={'relative'}>
        <div className='flex flex-wrap gap-1 items-center justify-start'>
          <div className='whitespace-nowrap'>
            <span className={cn('text-lg font-bold px-1')}>{mapPrice}</span>
          </div>
        </div>
        <div className={'absolute top-0 right-0 w-full h-full flex items-center justify-start'}>
          <div className={'w-1/4 max-w-[80px] opacity-80'}>
            <Image src={StrikeThroughPrice} alt='Strike Through Price'/>
          </div>
        </div>
      </div>
      <div className={'flex justify-start'}>
        <div className='text-2xl lg:text-3xl whitespace-nowrap'>
          {/*<span className={'font-bold'} style={{color: 'rgba(4,43,138, 1)'}}>*/}
          <span className={'font-bold'} style={{color: 'rgb(63,3,3)'}}>
            {`${specialPriceText}: ${salePrice}`}
          </span>
        </div>
      </div>
    </>
  );
};

const DealerPricing = ({product}) => {
  const selectedVariant = product?.selectedVariant ?? product;
  const dealer_price = selectedVariant?.price_dealer ?? selectedVariant?.priceDealer;

  const {sale_price, map_price} = useAllPrices(selectedVariant);
  let isSaleEnabled = priceHelper.saleEnabledFor(selectedVariant);

  return (
    <div>
      <div className='flex flex-row gap-3 items-center'>
        <div className='whitespace-nowrap'>
          <span className={'line-through ml-1'}>{`MAP: ${niceMoney(map_price)}`}</span>
        </div>

        {isSaleEnabled && (
          <div className='text-gray-700 flex border-left'>
            SALE PRICE:
            <span className={'line-through ml-1'}>{niceMoney(sale_price)}</span>
          </div>
        )}
      </div>

      <div className='flex items-center gap-2 text-lg lg:text-3xl whitespace-nowrap'>
        <span className={cn('font-bold text-skar-blue')}>{niceMoney(dealer_price)}</span>

        <div className={'bg-skar-blue text-white rounded-full text-xs px-4 py-1'}>DEALER PRICE</div>
      </div>
    </div>
  );
};

const useAllPrices = (selectedVariant) => {
  const priceMap = selectedVariant?.price_map ?? selectedVariant?.priceMap;
  const priceMsrp = selectedVariant?.price_msrp ?? selectedVariant?.priceMsrp;
  const sale_price = formatPriceNumeric(priceHelper.getFinalPrice(selectedVariant));
  const map_price = formatPriceNumeric(priceMap);
  const msrp_price = formatPriceNumeric(priceMsrp);

  return {
    sale_price,
    map_price,
    msrp_price
  };
};
